import React, { useState, useEffect, useContext } from "react";
import moment from "moment-timezone";

import { ModalWizardStepRenderProps } from "components/ModalWizard/ModalWizard";
import { CompletedWizardData } from "../BookingWizard";
import {
	SingleSessionPriceBase,
	flagValidationErrorsInSingleSessionPriceBase
} from "types-and-validators/SingleSessionPriceBase";

import { SessionType } from "types-and-validators/SessionType";
import { FieldOption } from "components/FieldSelect/FieldSelect";
import FieldSelect from "components/FieldSelect";
import SelectedEventSummary from "components/SelectedEventSummary";
import { useAccountStatus } from "hooks/useAccountStatus";
import { formatSessionPriceAndDuration } from "services/formatSessionPriceAndDuration";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import terminology from "terminology.json";

import { useSessionTypesAndPrices } from "hooks/useSessionTypesAndPrices";
import { useDefaultTimezone } from "hooks/useDefaultTimezone";
import { validate } from "validation/validate";
import { FieldSelectContainer } from "./StepChooseSessionDuration.styles";

const defaultCountryCodeUntilAccountStatusLoads = "GB";

function StepChooseSessionDuration({
	wizardData: { mentorId, sessionType, eventToBook },
	enableProgressToNextStep,
	disableProgressToNextStep
}: ModalWizardStepRenderProps<CompletedWizardData>) {
	const {
		isWaiting: sessionTypesAndPricesWaiting,
		isLoading: sessionTypesAndPricesLoading,
		isError: sessionTypesAndPricesError,
		output: sessionTypesAndPrices
	} = useSessionTypesAndPrices(mentorId);

	const defaultTimezone = useDefaultTimezone();
	const { locationCountryCode } = useAccountStatus();

	const [{ uid }] = useContext(AuthContext);

	if (!sessionType) {
		throw new Error("No session type");
	}

	const [currentOptions, setCurrentOptions] = useState<FieldOption[]>(
		generateOptions(
			sessionType,
			sessionTypesAndPrices,
			locationCountryCode
				? locationCountryCode
				: defaultCountryCodeUntilAccountStatusLoads
		)
	);

	const pricesStr = JSON.stringify({
		sessionTypesAndPrices
	});
	const sessionTypeStr = sessionType ? JSON.stringify(sessionType) : undefined;
	useEffect(() => {
		const { sessionTypesAndPrices } = JSON.parse(pricesStr);

		const sessionTypeDecoded = sessionTypeStr
			? JSON.parse(sessionTypeStr)
			: undefined;

		if (!sessionTypeDecoded) {
			throw new Error("No session type available");
		}

		setCurrentOptions(
			generateOptions(
				sessionTypeDecoded,
				sessionTypesAndPrices,
				locationCountryCode
					? locationCountryCode
					: defaultCountryCodeUntilAccountStatusLoads
			)
		);
	}, [pricesStr, setCurrentOptions, locationCountryCode, sessionTypeStr]);

	useEffect(() => {
		const selectedOption = currentOptions.find(opt => !!opt.selected);
		const selectedOptionValue = selectedOption
			? selectedOption.value
			: undefined;

		if (selectedOptionValue && typeof selectedOptionValue === "string") {
			if (!eventToBook) {
				throw new Error("No eventToBook");
			}

			const selectedOptionParsed: unknown = JSON.parse(selectedOptionValue);

			if (!inputIsSinglesessionPrice(selectedOptionParsed, uid)) {
				console.log("Unexpected selected option format", selectedOptionParsed);
				throw new Error("Unexpected selected option format");
			}

			/* Update end-time of the session to account for what the user chose
		       in this step (otherwise, it would be one-hour after the start-time) */
			eventToBook.end = moment(eventToBook.start)
				.clone()
				.add(selectedOptionParsed.sessionDurationMinutes, "minutes")
				.toDate();

			const selectedDurationAndPrice = {
				...selectedOptionParsed,
				type: "cash" as "cash"
			};

			enableProgressToNextStep({
				selectedDurationAndPrice,
				eventToBook
			});
		} else {
			disableProgressToNextStep();
		}
	}, [
		enableProgressToNextStep,
		disableProgressToNextStep,
		currentOptions,
		eventToBook,
		uid
	]);

	return (
		<>
			{/* Don't show end-time because it is currently one hour after the start-time (but it should depend on what the user chooses in this step) */}
			<SelectedEventSummary
				eventToBook={eventToBook}
				showEndTime={false}
				timezone={defaultTimezone}
			/>
			<FieldSelectContainer>
				<FieldSelect
					allowMultipleSelections={false}
					isLoading={
						sessionTypesAndPricesLoading || sessionTypesAndPricesWaiting
					}
					isError={sessionTypesAndPricesError}
					currentOptions={currentOptions}
					onNewCurrentOptions={setCurrentOptions}
				/>
			</FieldSelectContainer>
		</>
	);
}

export default StepChooseSessionDuration;

function generateOptions(
	sessionType: SessionType,
	sessionTypesAndPrices: ReturnType<typeof useSessionTypesAndPrices>["output"],
	locationCountryCode: string
) {
	const cashOptions = sessionTypesAndPrices.singleSessionPrices
		.filter(
			singleSessionPrice => singleSessionPrice.sessionTypeId === sessionType.id
		)
		.map(opt => ({ ...opt, type: "cash" as "cash" }));
	const allOptions = [...cashOptions].sort(
		(a, b) => a.sessionDurationMinutes - b.sessionDurationMinutes
	);

	const output = allOptions.map(option => {
		const value = getValueFromSingleSessionPrice(option);
		const text =
			formatSessionPriceAndDuration({
				singleSessionPrice: option,
				locationCountryCode
			}) + (option.guestUid ? ` - ${terminology.mentee}-specific price` : "");

		const jsonValue = JSON.stringify(value);
		return {
			id: jsonValue,

			// TODO:WV:20250305:Try to move away from jsonValue (which is just to pacify typescript) back to "value"
			value: jsonValue,
			text
		};
	});

	return output;
}

function getValueFromSingleSessionPrice({
	id,
	price,
	currency,
	sessionDurationMinutes,
	sessionTypeId
}: SingleSessionPriceBase) {
	return {
		id,
		price,
		currency,
		sessionDurationMinutes,
		sessionTypeId
	};
}

function inputIsSinglesessionPrice(
	input: unknown,
	uid: string | undefined
): input is SingleSessionPriceBase {
	if (
		validate(input, {
			doValidate: flagValidationErrorsInSingleSessionPriceBase,
			auth: { uid },
			withErrors: errors => {
				console.log("singlesessionprice errors", errors);
			}
		})
	) {
		return true;
	}
	return false;
}
